import React, { useContext, useState } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';
import { LessonMode } from '../../Constants';
import PlayerService from '../services/PlayerService';
import ShowPreviousResponseButton from './ShowPreviousResponseButton';
import ShowPreviousResponseConfirmDialog from './ShowPreviousResponseConfirmDialog';
import PreviewUnavailableDialog from './PreviewUnavailableDialog';
import { register } from '../../i18n';

const t = register('GlobalQuestionLabels');

const ResponseDependencyControls = observer(({ lessonElementId }) => {
  const {
    lessonManager,
    responseManager
  } = useContext(MobXProviderContext);

  const [showPreviousResponseConfirm, setShowPreviousResponseConfirm] = useState(false);
  const [showPreviewUnavailable, setShowPreviewUnavailable] = useState(false);

  const model = lessonManager.getSlideModel(lessonElementId);
  const lessonElementState = responseManager.getLessonElementState(lessonElementId);
  const responseDependencyValue = lessonManager.lessonOptions?.responseDependency?.[lessonElementId];
  let hasResponseDependency = false;
  let isDifferentLessonSource = false;

  if (responseDependencyValue) {
    if (typeof responseDependencyValue === 'object') {
      if (responseDependencyValue.source === 'inLesson') {
        hasResponseDependency = responseDependencyValue.questionId;
      } else if (responseDependencyValue.source === 'differentLesson') {
        isDifferentLessonSource = true;
        hasResponseDependency = responseDependencyValue.lessonId &&
          responseDependencyValue.questionForeverId &&
          responseDependencyValue.lessonId !== '' &&
          responseDependencyValue.questionForeverId !== '';
      }
    } else {
      hasResponseDependency = responseDependencyValue !== null && responseDependencyValue !== '';
    }
  }

  if (!hasResponseDependency || !lessonElementState ||
    lessonManager.playerMode === LessonMode.SCORING ||
    lessonManager.playerMode === LessonMode.REVIEW) {
    return null;
  }

  const handleShowPreviousResponse = async () => {
    const questionBehavior = lessonElementState.questionBehavior;
    if (!questionBehavior || !questionBehavior.hasResponseDependency(model, lessonElementId)) {
      return;
    }

    if (isDifferentLessonSource) {
      if (PlayerService.isActivityMode()) {
        await questionBehavior.fetchPriorResponsesFromDifferentLesson(responseDependencyValue, lessonElementId);
      } else {
        setShowPreviewUnavailable(true);
      }
      return;
    }

    if (questionBehavior.hasCurrentContent(lessonElementId)) {
      setShowPreviousResponseConfirm(true);
    } else {
      questionBehavior.populateWithPreviousResponse(lessonElementId, model);
    }
  };

  return (
    <>
      <div className='response-dependency-controls'>
        <ShowPreviousResponseButton
          lessonElementId={lessonElementId}
          onShowPreviousResponse={handleShowPreviousResponse}
          isDisabled={lessonElementState.isSubmitted}
        />
      </div>
      <ShowPreviousResponseConfirmDialog
        isOpen={showPreviousResponseConfirm}
        onConfirm={() => {
          setShowPreviousResponseConfirm(false);
          lessonElementState.questionBehavior.populateWithPreviousResponse(lessonElementId, model);
        }}
        onCancel={() => setShowPreviousResponseConfirm(false)}
        message={t('showPreviousResponseWarning')}
      />
      <PreviewUnavailableDialog
        isOpen={showPreviewUnavailable}
        onClose={() => setShowPreviewUnavailable(false)}
        message="This feature is only available during live question answering sessions. It cannot be previewed in this mode."
      />
    </>
  );
});

export default ResponseDependencyControls;
